/**
 * Strategy questionnaire header — top bar, stage progress segments, stage label.
 *
 * Belongs here: header layout and stage progress UI.
 * Does not belong here: answer handling or flow rules.
 */
import RenderHTML from '../../../../../components/ui-components/RenderHTML'
import { expertReviewSafeAreaTopStyle } from '../../shared/expertReviewSafeArea'
import type { StrategyQuestionnairePayload } from '../../shared/types/questionnaire.types'
import { ExpertReviewTopBar } from '../../shared/components/ExpertReviewTopBar'
import { ExpertReviewScreen } from '../../analytics/expertReviewEvents'
import { getStageIconSvg } from '../setup/stageAssets'

interface StrategyQuestionnaireHeaderProps {
  rawQuestionnaireData: StrategyQuestionnairePayload
  totalStages: number
  stageIndex: number
  progressInStage: number
  stageLabel: string
  positionInStage: number
  visibleLength: number
  hideStageLabel?: boolean
  onBack: () => void
  testLocatorBack?: string
  testLocatorMenu?: string
}

export function StrategyQuestionnaireHeader({
  rawQuestionnaireData,
  totalStages,
  stageIndex,
  progressInStage,
  stageLabel,
  positionInStage: _positionInStage,
  visibleLength: _visibleLength,
  hideStageLabel = false,
  onBack,
  testLocatorBack,
  testLocatorMenu
}: StrategyQuestionnaireHeaderProps) {
  const segmentCount = Math.max(1, totalStages)
  const stageId = rawQuestionnaireData?.stages?.[stageIndex]?.id ?? `stage-${stageIndex}`

  return (
    <div
      style={expertReviewSafeAreaTopStyle()}
      className='flex flex-col gap-4 bg-white px-5 pb-3'
    >
      <ExpertReviewTopBar
        onBack={onBack}
        menu="strategy"
        analyticsScreen={ExpertReviewScreen.StrategyQuestionnaire}
        testLocatorBack={testLocatorBack}
        testLocatorMenu={testLocatorMenu}
      />

      <div className='flex gap-1 w-full'>
        {Array.from({ length: segmentCount }, (_, segmentIndex) => {
          const isPast = stageIndex > segmentIndex
          const isCurrent = stageIndex === segmentIndex
          const fill = isPast ? 1 : isCurrent ? progressInStage : 0
          return (
            <div
              key={segmentIndex}
              className='flex-1 h-1.5 rounded-[24px] bg-[#F0F4F7] overflow-hidden'
              role='progressbar'
              aria-valuenow={fill * 100}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                className='h-full rounded-full bg-[#232323] transition-[width] duration-200 ease-out motion-reduce:transition-none'
                style={{ width: `${Math.min(100, Math.max(0, fill * 100))}%` }}
              />
            </div>
          )
        })}
      </div>

      {!hideStageLabel ? (
        <div className='flex items-center gap-2'>
          <span className='text-gray-500' aria-hidden>
            <RenderHTML innerHTMLText={getStageIconSvg(stageId, 'header')} />
          </span>
          <span className='text-sm font-medium text-gray-600'>{stageLabel}</span>
        </div>
      ) : null}
    </div>
  )
}
